import type { ReportGroup, ReportType } from "../../../../../../../api/types/svTypes.ts";

export interface ReportSelectorItem {
  reportId: string;
  name: string;
  groupId: string;
  reportType?: ReportType;
}

export const matchesReportFilter = (report: ReportSelectorItem, textFilter: string): boolean =>
  report.name.toLowerCase().indexOf(textFilter) > -1 || report.reportId.toLowerCase() === textFilter;

export const getFilteredGroupedReports = (
  reports: ReportSelectorItem[] | undefined,
  groups: ReportGroup[] | undefined,
  textFilter: string
) => {
  if (!reports || !groups) {
    return undefined;
  }

  const result: Record<string, ReportSelectorItem[]> = {};
  const filteredReports = !textFilter ? reports : reports.filter((f) => matchesReportFilter(f, textFilter));

  [...groups.sort((a, b) => a.order - b.order)].forEach((g) => {
    const groupReports = filteredReports.filter((r) => r.groupId === g.id).sort((a, b) => a.name.localeCompare(b.name));
    if (groupReports.length > 0) {
      result[g.caption] = groupReports;
    }
  });

  return Object.entries(result);
};

export const replaceReportIdInUrl = (
  currentUrl: string,
  prevReportId: string | undefined,
  newReportId: string
): string => {
  const urlSegments = currentUrl.split("/");
  const prevReportIdIndex = urlSegments.indexOf(prevReportId || "new");

  if (prevReportIdIndex !== -1) {
    urlSegments[prevReportIdIndex] = newReportId;
  }

  return urlSegments.join("/");
};

export const handleArrowDownMenuNavigation = (
  listItemNodes: Element[],
  selectedReportName: string | undefined,
  setSelectedReportName: (name: string) => void
) => {
  const indexOfSelectedReport = listItemNodes.findIndex((r) => r.ariaLabel === selectedReportName);
  const nextReport = listItemNodes[indexOfSelectedReport + 1] ?? listItemNodes[0];
  setSelectedReportName(nextReport?.ariaLabel || "");
  nextReport?.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

export const handleArrowUpMenuNavigation = (
  listItemNodes: Element[],
  selectedReportName: string | undefined,
  setSelectedReportName: (name: string) => void
) => {
  const indexOfSelectedReport = listItemNodes.findIndex((r) => r.ariaLabel === selectedReportName);
  const previousReport = listItemNodes[indexOfSelectedReport - 1] ?? listItemNodes[listItemNodes.length - 1];
  setSelectedReportName(previousReport?.ariaLabel || "");
  previousReport?.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

export const handleEnterMenuNavigation = (
  listItemNodes: Element[],
  selectedReportName: string | undefined,
  onSelectNewReport: (reportId: string) => void
) => {
  const indexOfSelectedReport = listItemNodes.findIndex((r) => r.ariaLabel === selectedReportName);
  const reportId = listItemNodes[indexOfSelectedReport]?.attributes.getNamedItem("data-report-id")?.value;
  if (!reportId) {
    return;
  }
  onSelectNewReport(reportId);
};

export const getGroupedListItemNodes = (listRef: HTMLUListElement | null) => {
  return Array.from(listRef?.children || [])
    .map((c) => c.children[0]?.children)
    .filter((c) => !!c)
    .flatMap((c) => Array.from(c || []))
    .filter((c) => c.role === "option");
};

export const getNonGroupedListItemNodes = (listRef: HTMLUListElement | null) => {
  return Array.from(listRef?.children || []).filter((c) => c.role === "option");
};
